import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import pluckrLogo from '../assets/images/pluckr-logo.png';
import ThemeToggle from '@/components/ThemeToggle';
import { useThemeMode } from '@/hooks/useThemeMode';

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { resolvedTheme } = useThemeMode();
  const isDark = resolvedTheme === 'dark';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const API_URL = useMemo(() => {
    return (
      process.env.REACT_APP_BACKEND_URL ||
      (window.location.hostname === 'localhost'
        ? 'http://localhost:5000'
        : 'https://api.pluckr.ai')
    );
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      toast.error('Invalid reset link.');
      return;
    }

    if (!password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.message || 'Reset failed');
        setIsLoading(false);
        return;
      }

      toast.success('Password reset successful! Please log in.');
      setTimeout(() => {
        navigate('/early-access');
      }, 1200);
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error('Network error. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-background grid-pattern" data-testid="reset-password-page">
      <Toaster
        position="top-right"
        theme={resolvedTheme}
        toastOptions={{
          style: {
            background: isDark ? '#282929' : '#ffffff',
            color: isDark ? '#ffffff' : '#1a1a1a',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #e5e5e5',
          },
        }}
      />

      <ThemeToggle className="fixed top-4 right-4 z-[60]" />

      {/* Top-left brand */}
      <div className="px-8 pt-7">
        <Link
          to="/"
          className="inline-flex items-center gap-3 text-black/90 dark:text-white"
          style={{ fontFamily: "'Lora', Georgia, serif" }}
        >
          <img src={pluckrLogo} alt="Pluckr" className="h-9 w-auto" />
          <span className="text-[22px] font-bold tracking-[-0.01em]">Pluckr</span>
        </Link>
      </div>

      <div className="max-w-[1200px] mx-auto px-8 pb-16 pt-10">
        <div className="flex flex-col lg:flex-row items-center lg:items-start justify-between gap-14">
          {/* Left headline */}
          <div className="flex-1 w-full lg:pt-10">
            <h1
              className="text-[56px] sm:text-[72px] font-black leading-[0.95] tracking-[-0.03em]"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <span className="block text-black/80 dark:text-white">Set a new</span>
              <span className="block" style={{ color: '#9be38f' }}>
                password
              </span>
            </h1>
          </div>

          {/* Right card */}
          <div className="w-full max-w-[420px]">
            <div
              className="bg-white dark:bg-[#282929] rounded-[18px] shadow-[0_20px_60px_rgba(0,0,0,0.25)] border border-black/5 dark:border-white/15 p-8"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <form onSubmit={handleSubmit} className="space-y-4">
                <p className="text-[14px] text-black/70 dark:text-white/70 mb-6">
                  Enter a new password for your account.
                </p>

                <div className="space-y-2">
                  <label className="text-[12px] font-medium text-black/70 dark:text-white/70">New password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-11 rounded-md border border-black/10 dark:border-white/20 bg-white dark:bg-[#282929] text-black dark:text-white px-3 text-[14px] outline-none focus:border-black/20 dark:focus:border-white/30 placeholder:text-black/40 dark:placeholder:text-white/50"
                    placeholder="At least 6 characters"
                    minLength={6}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[12px] font-medium text-black/70 dark:text-white/70">Confirm new password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full h-11 rounded-md border border-black/10 dark:border-white/20 bg-white dark:bg-[#282929] text-black dark:text-white px-3 text-[14px] outline-none focus:border-black/20 dark:focus:border-white/30 placeholder:text-black/40 dark:placeholder:text-white/50"
                    placeholder="Confirm your password"
                    minLength={6}
                    required
                    disabled={isLoading}
                  />
                </div>

                <button
                  type="submit"
                  className="w-full h-11 rounded-full font-semibold text-[13px] text-[#1a1a1a] transition-colors disabled:opacity-50 hover:brightness-95"
                  style={{ backgroundColor: 'var(--pluckr-green)' }}
                  disabled={isLoading}
                >
                  {isLoading ? 'Resetting...' : 'Reset password'}
                </button>

                <p className="text-[11px] text-black/45 dark:text-white/60 leading-snug pt-4">
                  Remember your password?{' '}
                  <Link
                    to="/early-access"
                    className="font-semibold text-black/75 dark:text-white hover:text-black dark:hover:text-white"
                  >
                    Back to login
                  </Link>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
